import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Shield, Activity, TrendingUp, FileText, CheckCircle2, ChevronDown, Award } from "lucide-react";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const handleToggle = () => setIsOpen(!isOpen);

  const platformItems = [
    { name: "Vendor Risk Intelligence", path: "/platform/risk", icon: Shield, desc: "Composite scoring & real-time monitoring" },
    { name: "Vendor Performance Analytics", path: "/platform/performance", icon: Activity, desc: "SLA tracking & automated scorecards" },
    { name: "Spend Analytics & Cost Intelligence", path: "/platform/spend", icon: TrendingUp, desc: "Consolidation & maverick spend detection" },
    { name: "Compliance & Audit Automation", path: "/platform/compliance", icon: CheckCircle2, desc: "Centralized certification & tracking" },
    { name: "Vendor Onboarding & KYC", path: "/platform/onboarding", icon: Award, desc: "Guided intake & verification" },
    { name: "Contract Lifecycle Management", path: "/platform/contract", icon: FileText, desc: "Renewal tracking & terms extraction" }
  ];

  const industryItems = [
    { name: "Manufacturing", path: "/industries/manufacturing" },
    { name: "Retail & E-commerce", path: "/industries/retail" },
    { name: "Healthcare & Pharma", path: "/industries/healthcare" },
    { name: "BFSI", path: "/industries/bfsi" },
    { name: "IT & Technology", path: "/industries/it" },
    { name: "Logistics & Supply Chain", path: "/industries/logistics" }
  ];

  const resourceItems = [
    { name: "Blog / Resource Center", path: "/resources/blog" },
    { name: "Whitepapers & Reports", path: "/resources/whitepapers" },
    { name: "ROI Calculator", path: "/resources/roi" },
    { name: "Help Center / Docs", path: "/resources/docs" },
    { name: "Webinars", path: "/resources/webinars" }
  ];

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#05050A]/85 border-b border-white/10 transition-colors duration-300" data-testid="header-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
            <div className="relative w-8 h-8 flex items-center justify-center">
              {/* Mountain A Mark logo symbol */}
              <svg viewBox="0 0 100 100" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="50,10 90,90 10,90" fill="#4B2E83" />
                <polygon points="50,10 90,90 65,90" fill="#7C3AED" />
                <polygon points="68,68 85,90 55,90" fill="#EA7C3D" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-wider text-white font-display">AROVENIQ</span>
              <span className="text-[9px] text-[#ABA3C2] tracking-[0.25em] -mt-1 font-mono flex items-center">
                SOLUTIONS
                <span className="inline-block w-1 h-1 bg-[#EA7C3D] rounded-full mx-1"></span>
                Procurement
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-1 items-center">
            
            {/* Platform Dropdown */}
            <div className="relative group/menu">
              <button 
                data-testid="nav-platform-trigger"
                onClick={() => setActiveDropdown(activeDropdown === 'platform' ? null : 'platform')}
                className="flex items-center px-4 py-2 text-sm font-medium text-[#ABA3C2] hover:text-white transition-colors duration-200"
              >
                Platform <ChevronDown className="ml-1 w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full mt-2 w-[480px] p-4 rounded-2xl bg-[#0A0A0F] border border-white/10 shadow-2xl opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-300 grid grid-cols-2 gap-4">
                {platformItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-platform-${item.path.split('/').pop()}`}
                    className="p-3 rounded-xl hover:bg-white/5 transition-all duration-200 flex flex-col group/item"
                  >
                    <div className="flex items-center space-x-2">
                      <item.icon className="w-4 h-4 text-[#7C3AED] group-hover/item:text-[#EA7C3D] transition-colors duration-200" />
                      <span className="text-sm font-semibold text-white">{item.name}</span>
                    </div>
                    <span className="text-xs text-zinc-500 mt-1 pl-6">{item.desc}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Industries Dropdown */}
            <div className="relative group/menu">
              <button 
                data-testid="nav-industries-trigger"
                className="flex items-center px-4 py-2 text-sm font-medium text-[#ABA3C2] hover:text-white transition-colors duration-200"
              >
                Industries <ChevronDown className="ml-1 w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full mt-2 w-64 p-3 rounded-2xl bg-[#0A0A0F] border border-white/10 shadow-2xl opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-300 space-y-1">
                {industryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-industries-${item.path.split('/').pop()}`}
                    className="block px-4 py-2 text-sm text-[#ABA3C2] hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Case Studies */}
            <Link 
              to="/case-studies" 
              data-testid="nav-case-studies"
              className="px-4 py-2 text-sm font-medium text-[#ABA3C2] hover:text-white transition-colors duration-200"
            >
              Case Studies
            </Link>

            {/* Resources Dropdown */}
            <div className="relative group/menu">
              <button 
                data-testid="nav-resources-trigger"
                className="flex items-center px-4 py-2 text-sm font-medium text-[#ABA3C2] hover:text-white transition-colors duration-200"
              >
                Resources <ChevronDown className="ml-1 w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full mt-2 w-64 p-3 rounded-2xl bg-[#0A0A0F] border border-white/10 shadow-2xl opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-300 space-y-1">
                {resourceItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-resources-${item.path.split('/').pop()}`}
                    className="block px-4 py-2 text-sm text-[#ABA3C2] hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <Link 
              to="/pricing" 
              data-testid="nav-pricing"
              className="px-4 py-2 text-sm font-medium text-[#ABA3C2] hover:text-white transition-colors duration-200"
            >
              Pricing
            </Link>

            {/* About Us */}
            <Link 
              to="/about" 
              data-testid="nav-about"
              className="px-4 py-2 text-sm font-medium text-[#ABA3C2] hover:text-white transition-colors duration-200"
            >
              About Us
            </Link>

          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <Link
              to="/contact"
              data-testid="nav-book-demo-cta"
              className="relative px-6 py-2.5 rounded-full bg-gradient-to-r from-[#4B2E83] via-[#7C3AED] to-[#EA7C3D] text-white text-sm font-semibold tracking-wide hover:shadow-[0_0_20px_rgba(124,58,237,0.4)] transition-all duration-300 ease-out flex items-center group"
            >
              Book a Demo
              <span className="ml-2 group-hover:translate-x-1.5 transition-transform duration-200">→</span>
            </Link>
          </div>

          {/* Hamburger Menu Mobile */}
          <div className="flex lg:hidden">
            <button
              data-testid="mobile-menu-toggle"
              onClick={handleToggle}
              className="p-2 rounded-lg text-[#ABA3C2] hover:text-white focus:outline-none"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer */}
      {isOpen && (
        <div className="fixed inset-0 top-20 z-40 w-full h-[calc(100vh-5rem)] bg-[#05050A]/95 backdrop-blur-2xl border-t border-white/10 lg:hidden overflow-y-auto" data-testid="mobile-menu-drawer">
          <div className="px-4 py-6 space-y-4">
            
            {/* Platform Accordion */}
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#6E6785] px-3">Platform</div>
              <div className="grid grid-cols-1 gap-1 pl-3">
                {platformItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-platform-${item.path.split('/').pop()}`}
                    className="py-2 text-sm text-[#ABA3C2] hover:text-white"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Industries Accordion */}
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#6E6785] px-3">Industries</div>
              <div className="grid grid-cols-1 gap-1 pl-3">
                {industryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-industry-${item.path.split('/').pop()}`}
                    className="py-2 text-sm text-[#ABA3C2] hover:text-white"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Resources Accordion */}
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#6E6785] px-3">Resources</div>
              <div className="grid grid-cols-1 gap-1 pl-3">
                {resourceItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-resource-${item.path.split('/').pop()}`}
                    className="py-2 text-sm text-[#ABA3C2] hover:text-white"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 space-y-3">
              <Link
                to="/case-studies"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-base font-medium text-[#ABA3C2] hover:text-white"
              >
                Case Studies
              </Link>
              <Link
                to="/pricing"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-base font-medium text-[#ABA3C2] hover:text-white"
              >
                Pricing
              </Link>
              <Link
                to="/about"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-base font-medium text-[#ABA3C2] hover:text-white"
              >
                About Us
              </Link>
            </div>

            <div className="pt-6">
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-book-demo-cta"
                className="block w-full text-center px-6 py-3 rounded-full bg-gradient-to-r from-[#4B2E83] via-[#7C3AED] to-[#EA7C3D] text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200"
              >
                Book a Demo →
              </Link>
            </div>

          </div>
        </div>
      )}
    </header>
  );
}